import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Icons, Tooltip, Typography } from '../../../../../../stories/atoms';
import {
  ColumnFlexContainer,
  ContainerV2,
  RowFlexContainer,
} from '../../../../../../stories/atoms/containerV2/ContainerV2';
import { homeTranslations } from '../../../../../../translations';
import { formatValue } from '../../../../../../utils';
import { CustomerEngagementTranslation, EngagementMetrics } from '../CustomerEngagement';
import { SectionContainer } from '../CustomerEngagement.styles';

const { customerEngagement: customerEngagementranslations } = homeTranslations;

type SubMetric = {
  name: string;
  value: number;
};

type MetricSectionProps = {
  metricName: EngagementMetrics;
  subMetric1: SubMetric;
  subMetric2: SubMetric;
  subMetric3: SubMetric;
  icon: Icons;
  isMobileView: boolean;
  tooltipText: string;
  isSliderView?: boolean;
  sendMetricDetails?: (metricName: EngagementMetrics) => void;
};

export const MetricSection: FC<MetricSectionProps> = ({
  metricName,
  subMetric1,
  subMetric2,
  subMetric3,
  icon,
  isMobileView,
  tooltipText,
  sendMetricDetails = () => {},
  isSliderView = false,
}) => {
  const { t } = useTranslation();
  const getMetricName = (name: string) => {
    if (customerEngagementranslations[name as CustomerEngagementTranslation]) {
      return t(
        customerEngagementranslations[name as CustomerEngagementTranslation].key,
        customerEngagementranslations[name as CustomerEngagementTranslation].defaultValue,
      );
    } else {
      return name;
    }
  };

  return (
    <SectionContainer
      data-testid={`metric-section-${metricName}`}
      metricName={metricName}
      flex={1}
      borderRadius={[2]}
      padding={[2]}
      onClick={() => sendMetricDetails(metricName)}
      isSliderView={isSliderView}
    >
      <RowFlexContainer justifyContent="between">
        <ContainerV2 display="flex" flexDirection={isSliderView ? 'column' : 'row'}>
          <Icon icon={icon} size={isSliderView ? 'medium' : 'small'} />
          <Typography type={isSliderView ? 'button' : 'body2'} margin={[0, 0, 0, 1]}>
            {t(
              customerEngagementranslations[metricName as CustomerEngagementTranslation].key,
              customerEngagementranslations[metricName as CustomerEngagementTranslation].defaultValue,
            )}
          </Typography>
        </ContainerV2>
        <Tooltip
          arrowedToolTip
          text={
            <ColumnFlexContainer flexWrap="wrap" padding={[1, 2]}>
              <Typography color="white">
                {t(
                  customerEngagementranslations[tooltipText as CustomerEngagementTranslation].key,
                  customerEngagementranslations[tooltipText as CustomerEngagementTranslation].defaultValue,
                )}
              </Typography>
            </ColumnFlexContainer>
          }
        >
          <Icon icon={Icons.INFO} size="small" />
        </Tooltip>
      </RowFlexContainer>
      <ContainerV2 display="flex" flexDirection={isSliderView ? 'column' : 'row'} flex={1}>
        {!isMobileView && (
          <ColumnFlexContainer
            flex={1}
            alignItems={isSliderView ? 'start' : 'center'}
            justifyContent="center"
            key={subMetric1.name}
          >
            <Typography type="button">
              {formatValue(subMetric1.value, {
                locale: 'en-US',
                options: { minimumFractionDigits: 0, notation: 'compact' },
              })}
            </Typography>
            <Typography color="textGray">{getMetricName(subMetric1.name)}</Typography>
          </ColumnFlexContainer>
        )}
        {!isMobileView && (
          <ColumnFlexContainer
            flex={1}
            alignItems={isSliderView ? 'start' : 'center'}
            justifyContent="center"
            key={subMetric2.name}
          >
            <Typography type="button">
              {formatValue(subMetric2.value, {
                options: { minimumFractionDigits: 0, notation: 'compact' },
              })}
            </Typography>
            <Typography color="textGray">{getMetricName(subMetric2.name)}</Typography>
          </ColumnFlexContainer>
        )}
        <ColumnFlexContainer
          flex={1}
          alignItems={isSliderView ? 'start' : 'center'}
          justifyContent="center"
          key={subMetric3.name}
        >
          <Typography type="button">
            {formatValue(subMetric3.value, {
              locale: 'en-US',
              options: { minimumFractionDigits: 0, notation: 'compact' },
            })}
            %
          </Typography>
          <Typography color="textGray">{getMetricName(subMetric3.name)}</Typography>
        </ColumnFlexContainer>
      </ContainerV2>
    </SectionContainer>
  );
};
